import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  api,
  type AdminTenantRow,
  type AdminUserRow,
  type CreatedInvite,
  type InviteRow,
  type Role,
} from "../../api.ts";
import { useAuth } from "../../lib/auth.tsx";

const ROLES: Role[] = ["viewer", "member", "admin"];
const STATUS_PILL: Record<string, string> = { pending: "amber", accepted: "green", expired: "red" };

export function AdminUsersPage() {
  const { me, loading } = useAuth();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [tenants, setTenants] = useState<AdminTenantRow[]>([]);

  const [email, setEmail] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(async () => {
    const [u, i, t] = await Promise.all([
      api.get<{ users: AdminUserRow[] }>("/api/admin/users"),
      api.get<{ invites: InviteRow[] }>("/api/admin/invites"),
      api.get<{ tenants: AdminTenantRow[] }>("/api/admin/tenants"),
    ]);
    setUsers(u.users);
    setInvites(i.invites);
    setTenants(t.tenants);
    if (!tenantSlug && t.tenants[0]) setTenantSlug(t.tenants[0].slug);
  }, [tenantSlug]);

  useEffect(() => {
    if (me?.user.superAdmin) reload().catch((e) => setError(String(e)));
  }, [me, reload]);

  const showLink = (link: string) => {
    setLastLink(link);
    setCopied(false);
  };

  const copy = async () => {
    if (!lastLink) return;
    await navigator.clipboard.writeText(lastLink);
    setCopied(true);
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const inv = await api.post<CreatedInvite>("/api/admin/invites", {
        email,
        tenant_slug: tenantSlug,
        role,
      });
      showLink(inv.link);
      setEmail("");
      await reload();
    } catch (err: any) {
      setError(err?.message === "tenant_not_found" ? "Tenant inválido." : "Falha ao criar convite.");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (inv: InviteRow) => {
    setError(null);
    try {
      const created = await api.post<CreatedInvite>("/api/admin/invites", {
        email: inv.email,
        tenant_slug: inv.tenant_slug,
        role: inv.role,
      });
      showLink(created.link);
      await reload();
    } catch {
      setError("Falha ao gerar novo link.");
    }
  };

  const revoke = async (inv: InviteRow) => {
    await api.del(`/api/admin/invites/${inv.id}`);
    await reload();
  };

  if (loading) return <div className="center-loading">carregando…</div>;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.user.superAdmin) return <Navigate to="/" replace />;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "2rem 1.2rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <div>
          <p className="eyebrow">Administração</p>
          <h1 className="crown" style={{ margin: 0 }}>Usuários & convites</h1>
        </div>
        <Link to="/" className="btn secondary small" style={{ textDecoration: "none" }}>← Voltar</Link>
      </div>

      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h3>Convidar novo usuário</h3>
        <form className="form" style={{ maxWidth: "100%" }} onSubmit={onCreate}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: ".7rem", alignItems: "end" }}>
            <label>
              E-mail
              <input type="email" required value={email} placeholder="pessoa@empresa.com"
                     onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>
              Tenant
              <select value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)}>
                {tenants.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
              </select>
            </label>
            <label>
              Papel
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <button className="btn" type="submit" disabled={busy} style={{ height: "min-content" }}>
              {busy ? "Gerando…" : "Convidar"}
            </button>
          </div>
          {error && <div className="err">{error}</div>}
        </form>

        {lastLink && (
          <div style={{ marginTop: "1rem", padding: ".9rem 1rem", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)" }}>
            <p className="eyebrow" style={{ marginTop: 0 }}>Link do convite — envie para a pessoa</p>
            <div style={{ display: "flex", gap: ".6rem", alignItems: "center" }}>
              <input className="mono" readOnly value={lastLink}
                     style={{ flex: 1, padding: ".55rem .7rem", border: "1px solid var(--border-strong)", borderRadius: "var(--r-sm)", fontSize: ".8rem" }} />
              <button type="button" className="btn small accent" onClick={copy}>
                {copied ? "Copiado!" : "Copiar"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h3>Convites ({invites.length})</h3>
        {invites.length === 0 && <p className="sub">Nenhum convite ainda.</p>}
        {invites.map((inv) => (
          <div key={inv.id} className="list-row">
            <span>
              <strong>{inv.email}</strong>
              <span className="mono" style={{ marginLeft: ".6rem", opacity: .55, fontSize: ".78rem" }}>
                {inv.tenant_slug} · {inv.role}
              </span>
            </span>
            <span style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
              <span className={`pill ${STATUS_PILL[inv.status] ?? ""}`}>{inv.status}</span>
              {inv.status !== "accepted" && (
                <>
                  <button className="btn small secondary" onClick={() => regenerate(inv)}>Gerar link</button>
                  <button className="btn small secondary" onClick={() => revoke(inv)}>Revogar</button>
                </>
              )}
            </span>
          </div>
        ))}
      </div>

      <div className="card">
        <h3>Usuários ({users.length})</h3>
        {users.map((u) => (
          <div key={u.id} className="list-row">
            <span>
              <strong>{u.name ?? u.email}</strong>
              <span className="mono" style={{ marginLeft: ".6rem", opacity: .55, fontSize: ".78rem" }}>{u.email}</span>
            </span>
            <span style={{ display: "flex", gap: ".4rem", alignItems: "center" }}>
              {u.is_super_admin && <span className="pill">super</span>}
              {(u.memberships ?? []).map((m) => (
                <span key={m.slug} className="pill">{m.slug}:{m.role}</span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
